import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useLanguage, api } from '../context/AppContext';
import { Loader2, Swords, Flame, Droplets, Dumbbell, Zap, Target, UtensilsCrossed, Users, Trophy, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  swords: Swords, flame: Flame, droplets: Droplets, dumbbell: Dumbbell,
  zap: Zap, target: Target, utensils: UtensilsCrossed, default: Trophy,
};

export default function CollectiveChallengeCard({ compact = false }) {
  const { language } = useLanguage();
  const [challenge, setChallenge] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.get('/challenges/collective/active')
      .then(r => {
        setChallenge(r.data.challenge);
        setUserProgress(r.data.user_progress);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleJoin = async () => {
    if (!challenge) return;
    setJoining(true);
    try {
      await api.post(`/challenges/collective/${challenge.challenge_id}/join`);
      toast.success(language === 'fr' ? 'Tu es dans le défi!' : 'You joined the challenge!');
      const r = await api.get('/challenges/collective/active');
      setChallenge(r.data.challenge);
      setUserProgress(r.data.user_progress);
    } catch {
      toast.error('Erreur');
    } finally {
      setJoining(false);
    }
  };

  if (loading) return null;
  if (!challenge) return null;

  const Icon = ICON_MAP[challenge.icon] || ICON_MAP.default;
  const isCollective = challenge.type === 'collective';
  const pct = challenge.progress_pct || 0;
  const isCompleted = challenge.completed;
  const hasJoined = challenge.participants?.includes?.('') || !!userProgress;

  if (compact) {
    return (
      <Card
        className={`border ${isCompleted ? 'border-green-500/50 bg-green-950/20' : 'border-red-500/30 bg-gradient-to-r from-red-950/20 to-card'}`}
        data-testid="collective-challenge-card"
      >
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <Icon className={`w-5 h-5 ${isCompleted ? 'text-green-400' : 'text-red-400'}`} />
              </div>
              <div>
                <p className="text-sm font-heading text-foreground">{challenge.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${isCollective ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {isCollective ? 'Collectif' : 'Individuel'}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{challenge.progress}/{challenge.target}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <span className={`text-lg font-black ${isCompleted ? 'text-green-400' : 'text-red-400'}`}>{pct}%</span>
              <p className="text-[10px] text-muted-foreground">+{challenge.bonus_pts} pts</p>
            </div>
          </div>
          <Progress value={pct} className="h-1.5 mt-2" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`border-2 ${isCompleted ? 'border-green-500/50' : 'border-red-500/30'} overflow-hidden`}
      data-testid="collective-challenge-card"
    >
      <div className={`h-1 w-full ${isCompleted ? 'bg-green-500' : 'bg-gradient-to-r from-red-600 via-orange-500 to-red-600'}`} />
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${isCompleted ? 'text-green-400' : 'text-red-400'}`} />
            <span className="font-heading text-base text-foreground">{challenge.title}</span>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${isCollective ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
            {isCollective ? (language === 'fr' ? 'Collectif' : 'Collective') : (language === 'fr' ? 'Individuel' : 'Individual')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{challenge.description}</p>

        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">{language === 'fr' ? 'Progression' : 'Progress'}</span>
            <span className={`font-bold ${isCompleted ? 'text-green-400' : 'text-foreground'}`}>{challenge.progress}/{challenge.target}</span>
          </div>
          <Progress value={pct} className="h-2.5" />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{challenge.participants?.length || 0} participants</span>
          </div>
          <span className="text-xs font-bold text-amber-400">+{challenge.bonus_pts} pts bonus</span>
        </div>

        {challenge.individual_leaderboard?.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-border/50">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{language === 'fr' ? 'Top contributeurs' : 'Top contributors'}</p>
            {challenge.individual_leaderboard.slice(0, 3).map((p, i) => (
              <div key={p.user_id} className="flex items-center justify-between text-xs">
                <span className="text-foreground">#{i + 1} {p.user_name || 'Anonyme'}</span>
                <span className="font-bold text-primary">{p.progress}</span>
              </div>
            ))}
          </div>
        )}

        {!hasJoined && !isCompleted && (
          <Button
            onClick={handleJoin}
            disabled={joining}
            className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
            data-testid="join-challenge-btn"
          >
            {joining ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Swords className="w-4 h-4 mr-2" />}
            {language === 'fr' ? 'Rejoindre le défi!' : 'Join the challenge!'}
          </Button>
        )}

        {isCompleted && (
          <div className="text-center py-2 bg-green-500/10 rounded-lg" data-testid="challenge-completed">
            <Trophy className="w-6 h-6 text-green-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-green-400">{language === 'fr' ? 'Défi complété!' : 'Challenge completed!'}</p>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground text-center">
          {challenge.week_start} → {challenge.week_end}
          {challenge.ai_generated && <span className="ml-1 text-violet-400">IA</span>}
        </p>
      </CardContent>
    </Card>
  );
}
